import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { setMeta, setJsonLd, faqJsonLd } from "./seo";
import auditPreview from "./assets/audit-preview.png";
import aiPreview from "./assets/ai-preview.png";
import ContactForm from "./ContactForm";
import SiteFooter from "./SiteFooter";
import { offersByPillar, CONTACT_EMAIL } from "./offers";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400;1,700&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;1,9..40,300&display=swap');

  :root {
    --ink: #0f0e0d;
    --ink-muted: #5a5650;
    --ink-faint: #b8b2aa;
    --paper: #faf8f4;
    --paper-warm: #f2ede4;
    --accent: #c8392b;
    --accent-light: #f5e8e6;
    --rule: #ddd8cf;
    --serif: 'Playfair Display', Georgia, serif;
    --sans: 'DM Sans', system-ui, sans-serif;
  }

  .hp-root * { box-sizing: border-box; margin: 0; padding: 0; }
  .hp-root {
    font-family: var(--sans);
    background: var(--paper);
    color: var(--ink);
  }

  /* ── Nav ── */
  .hp-nav {
    position: sticky; top: 0; z-index: 50;
    border-bottom: 1px solid var(--rule);
    background: var(--paper); padding: 14px 0;
  }
  .hp-nav-inner {
    max-width: 1200px; margin: 0 auto; padding: 0 32px;
    display: flex; align-items: center; justify-content: space-between;
  }
  .hp-nav-logo {
    font-family: var(--serif); font-size: 20px; font-weight: 700;
    letter-spacing: -0.01em; color: var(--ink); text-decoration: none;
  }
  .hp-nav-links {
    display: flex; align-items: center; gap: 32px;
  }
  .hp-nav-link {
    font-size: 12px; font-weight: 500; letter-spacing: 0.1em;
    text-transform: uppercase; color: var(--ink-muted);
    text-decoration: none; transition: color 0.15s;
  }
  .hp-nav-link:hover { color: var(--accent); }
  .hp-nav-cta {
    font-size: 12px; font-weight: 500; letter-spacing: 0.1em;
    text-transform: uppercase; color: white;
    background: var(--accent); padding: 9px 20px;
    text-decoration: none; transition: opacity 0.15s;
  }
  .hp-nav-cta:hover { opacity: 0.85; }

  /* ── Hero ── */
  .hp-hero {
    max-width: 1200px; margin: 0 auto;
    padding: 80px 32px 0;
    display: grid; grid-template-columns: 1fr 1fr;
    gap: 0 80px; align-items: center;
    min-height: 520px;
  }

  .hp-hero-kicker {
    display: flex; align-items: center; gap: 12px; margin-bottom: 28px;
  }
  .hp-hero-kicker-line { flex: 0 0 32px; height: 2px; background: var(--accent); }
  .hp-hero-kicker-text {
    font-size: 11px; font-weight: 500; letter-spacing: 0.14em;
    text-transform: uppercase; color: var(--accent);
  }

  .hp-hero-title {
    font-family: var(--serif);
    font-size: clamp(38px, 4.5vw, 66px);
    font-weight: 900; line-height: 1.0; letter-spacing: -0.025em;
    color: var(--ink);
  }
  .hp-hero-title em { font-style: italic; color: var(--accent); }

  .hp-hero-right { padding-top: 8px; }

  .hp-hero-deck {
    font-size: 18px; font-weight: 300; line-height: 1.7;
    color: var(--ink-muted); border-left: 2px solid var(--rule);
    padding-left: 20px; margin-bottom: 12px;
  }
  .hp-hero-sub {
    font-size: 15px; line-height: 1.65; color: var(--ink-faint);
    padding-left: 22px; margin-bottom: 36px;
  }
  .hp-hero-cta {
    display: inline-block;
    background: var(--accent); color: white;
    font-size: 12px; font-weight: 500; letter-spacing: 0.12em;
    text-transform: uppercase; text-decoration: none;
    padding: 14px 32px;
    transition: opacity 0.15s;
  }
  .hp-hero-cta:hover { opacity: 0.85; }
  .hp-hero-ctas { display: flex; gap: 12px; flex-wrap: wrap; align-items: center; }
  .hp-hero-cta-ghost {
    display: inline-block; color: var(--ink);
    font-size: 12px; font-weight: 500; letter-spacing: 0.12em;
    text-transform: uppercase; text-decoration: none;
    padding: 13px 28px; border: 1px solid var(--ink);
    transition: color 0.15s, border-color 0.15s;
  }
  .hp-hero-cta-ghost:hover { color: var(--accent); border-color: var(--accent); }

  /* ── Divider ── */
  .hp-divider {
    max-width: 1200px; margin: 64px auto 0; padding: 0 32px;
    display: flex; align-items: center; gap: 24px;
  }
  .hp-divider hr { flex: 1; border: none; border-top: 1px solid var(--rule); }
  .hp-divider-ornament { font-family: var(--serif); font-size: 20px; color: var(--ink-faint); }

  /* ── Problem Statement ── */
  .hp-problem {
    max-width: 1200px; margin: 0 auto; padding: 64px 32px;
    display: grid; grid-template-columns: 1fr 1fr; gap: 0 80px; align-items: start;
  }
  .hp-problem-left h2 {
    font-family: var(--serif); font-size: clamp(28px, 3vw, 42px);
    font-weight: 700; line-height: 1.15; letter-spacing: -0.015em;
    color: var(--ink);
  }
  .hp-problem-left h2 em { font-style: italic; color: var(--accent); }
  .hp-problem-right p {
    font-size: 17px; line-height: 1.78; color: var(--ink-muted);
    margin-bottom: 18px;
  }
  .hp-problem-impact {
    font-family: var(--serif); font-size: 16px; font-style: italic;
    line-height: 1.5; color: var(--ink); background: var(--paper-warm);
    border-left: 3px solid var(--accent); padding: 16px 20px;
    margin-top: 8px;
  }

  /* ── Section Header ── */
  .hp-section-header {
    max-width: 1200px; margin: 0 auto; padding: 64px 32px 0;
  }
  .hp-section-kicker {
    display: flex; align-items: center; gap: 12px; margin-bottom: 16px;
  }
  .hp-section-kicker-line { flex: 0 0 32px; height: 2px; background: var(--accent); }
  .hp-section-kicker-text {
    font-size: 11px; font-weight: 500; letter-spacing: 0.14em;
    text-transform: uppercase; color: var(--accent);
  }
  .hp-section-title {
    font-family: var(--serif); font-size: clamp(28px, 3vw, 44px);
    font-weight: 900; line-height: 1.1; letter-spacing: -0.02em; color: var(--ink);
  }

  /* ── Use Cases ── */
  .hp-usecases {
    max-width: 1200px; margin: 0 auto; padding: 0 32px 0;
  }

  .hp-usecase {
    display: grid; grid-template-columns: 1fr 1fr;
    gap: 1px; background: var(--rule);
    border: 1px solid var(--rule); margin-top: 40px;
  }
  .hp-usecase.reverse { direction: rtl; }
  .hp-usecase.reverse > * { direction: ltr; }

  .hp-usecase-body {
    background: var(--paper); padding: 48px 48px;
    display: flex; flex-direction: column; justify-content: center;
  }
  .hp-usecase-tag {
    font-size: 10px; font-weight: 500; letter-spacing: 0.14em;
    text-transform: uppercase; color: var(--accent);
    display: flex; align-items: center; gap: 8px; margin-bottom: 20px;
  }
  .hp-usecase-tag::after { content: ''; flex: 0 0 24px; height: 1px; background: var(--accent); }
  .hp-usecase-title {
    font-family: var(--serif); font-size: clamp(20px, 2vw, 28px);
    font-weight: 700; line-height: 1.2; letter-spacing: -0.01em;
    color: var(--ink); margin-bottom: 16px;
  }
  .hp-usecase-desc {
    font-size: 16px; line-height: 1.7; color: var(--ink-muted); margin-bottom: 24px;
  }
  .hp-usecase-list {
    list-style: none; padding: 0; margin: 0;
    display: flex; flex-direction: column; gap: 8px;
  }
  .hp-usecase-list li {
    font-size: 14px; line-height: 1.55; color: var(--ink-muted);
    display: flex; align-items: flex-start; gap: 10px;
  }
  .hp-usecase-list li::before {
    content: '\u2014'; color: var(--accent); font-weight: 700; flex-shrink: 0;
  }
  .hp-usecase.no-image { grid-template-columns: 1fr; }
  .hp-usecase-promise {
    font-family: var(--serif); font-size: 17px; font-style: italic;
    color: var(--ink); margin-bottom: 14px;
  }
  .hp-usecase-offers {
    margin-top: 24px; padding-top: 18px; border-top: 1px solid var(--rule);
    display: flex; flex-wrap: wrap; gap: 8px 18px; align-items: baseline;
  }
  .hp-usecase-offers-label {
    font-size: 10px; font-weight: 500; letter-spacing: 0.14em;
    text-transform: uppercase; color: var(--ink-faint);
  }
  .hp-usecase-offers a {
    font-size: 14px; color: var(--accent); text-decoration: none;
  }
  .hp-usecase-offers a:hover { text-decoration: underline; }



  /* ── FAQ ── */
  .hp-faq {
    max-width: 1200px; margin: 32px auto 0; padding: 0 32px;
    display: grid; grid-template-columns: 1fr 1fr; gap: 0 64px;
  }
  .hp-faq-item { padding: 22px 0; border-top: 1px solid var(--rule); }
  .hp-faq-q {
    font-family: var(--serif); font-size: 19px; font-weight: 700;
    line-height: 1.3; color: var(--ink); margin-bottom: 8px;
  }
  .hp-faq-a { font-size: 15px; line-height: 1.7; color: var(--ink-muted); }

  .hp-usecase-image {
    background: var(--paper-warm);
    overflow: hidden; display: flex; align-items: center; justify-content: center;
    padding: 32px;
  }
  .hp-usecase-image img {
    width: 100%; display: block; border-radius: 2px;
    box-shadow: 0 8px 32px rgba(0,0,0,0.10);
  }

  /* ── Feature Cards ── */
  .hp-features {
    max-width: 1200px; margin: 40px auto 0; padding: 0 32px;
    display: grid; grid-template-columns: 1fr 1fr 1fr;
    gap: 1px; background: var(--rule);
    border: 1px solid var(--rule);
  }
  .hp-feature {
    background: var(--paper); padding: 36px 32px;
    transition: background 0.15s;
  }
  .hp-feature:hover { background: var(--paper-warm); }
  .hp-feature-num {
    font-family: var(--serif); font-size: 40px; font-weight: 900;
    color: var(--ink); opacity: 0.07; line-height: 1; margin-bottom: -8px;
  }
  .hp-feature-title {
    font-family: var(--serif); font-size: 18px; font-weight: 700;
    color: var(--ink); margin-bottom: 10px;
  }
  .hp-feature-text {
    font-size: 14px; line-height: 1.65; color: var(--ink-muted);
  }

  /* ── About ── */
  .hp-about {
    border-top: 1px solid var(--rule); border-bottom: 1px solid var(--rule);
    background: var(--ink); margin-top: 64px;
  }
  .hp-about-inner {
    max-width: 1200px; margin: 0 auto; padding: 80px 32px;
    display: grid; grid-template-columns: 280px 1fr; gap: 0 80px; align-items: start;
  }
  .hp-about-label {
    font-size: 11px; font-weight: 500; letter-spacing: 0.14em;
    text-transform: uppercase; color: var(--accent); margin-bottom: 16px;
    display: flex; align-items: center; gap: 8px;
  }
  .hp-about-label::after { content: ''; flex: 0 0 24px; height: 1px; background: var(--accent); }
  .hp-about-heading {
    font-family: var(--serif); font-size: clamp(28px, 3vw, 40px);
    font-weight: 700; line-height: 1.15; letter-spacing: -0.015em;
    color: var(--paper);
  }
  .hp-about-heading em { font-style: italic; color: #e87d72; }
  .hp-about-right p {
    font-size: 17px; line-height: 1.78; color: #b8b2aa; margin-bottom: 20px;
  }
  .hp-about-right p:last-child { margin-bottom: 0; }
  .hp-about-right strong { color: var(--paper); font-weight: 500; }

  /* ── Contact ── */
  .hp-contact {
    max-width: 1200px; margin: 0 auto; padding: 80px 32px 96px;
    display: grid; grid-template-columns: 1fr 1fr; gap: 0 80px; align-items: start;
  }
  .hp-contact-left h2 {
    font-family: var(--serif); font-size: clamp(28px, 3vw, 44px);
    font-weight: 900; line-height: 1.1; letter-spacing: -0.02em; color: var(--ink);
    margin-bottom: 20px;
  }
  .hp-contact-left h2 em { font-style: italic; color: var(--accent); }
  .hp-contact-left p {
    font-size: 16px; line-height: 1.7; color: var(--ink-muted);
    border-left: 2px solid var(--rule); padding-left: 20px;
  }

  .hp-contact-left p + p { margin-top: 16px; }
  .hp-contact-left a { color: var(--accent); }

  /* ── Responsive ── */
  @media (max-width: 960px) {
    .hp-hero, .hp-problem, .hp-about-inner, .hp-contact {
      grid-template-columns: 1fr; gap: 40px;
    }
    .hp-hero { padding: 48px 20px 0; min-height: unset; }
    .hp-usecase, .hp-usecase.reverse { grid-template-columns: 1fr; direction: ltr; }
    .hp-features { grid-template-columns: 1fr; }
    .hp-faq { grid-template-columns: 1fr; padding-left: 20px; padding-right: 20px; }
    .hp-usecase-body { padding: 32px 24px; }
    .hp-divider, .hp-section-header, .hp-usecases,
    .hp-problem, .hp-about-inner, .hp-contact { padding-left: 20px; padding-right: 20px; }
    .hp-nav-links { display: none; }
  }
`;

const homeFaqs = [
  {
    q: "What does Documore do?",
    a: "Documore verifies that your data, documents and AI systems agree before you act on them. We work in three areas: data integrity (lead lists, CRM and master data), decision integrity (BOMs, invoices, drawings and insurance submissions) and agentic integrity (chatbots, voice agents and RAG systems).",
  },
  {
    q: "What is data integrity?",
    a: "Data integrity means your records agree with each other and with reality: no duplicates, no stale contacts, and no conflicting values between systems. Documore checks records against multiple sources, such as two email verifiers and current employment data, before your team acts on them.",
  },
  {
    q: "What is decision integrity?",
    a: "Decision integrity means checking the inputs to a decision before you quote, pay or underwrite. Documore turns documents like BOMs, invoices and loss runs into verified data and flags anything that doesn't reconcile to its source.",
  },
  {
    q: "What is agentic integrity?",
    a: "Agentic integrity means your AI systems say and do what they should. Documore tests chatbots, voice agents and RAG assistants against your own policies and data, and reports where answers are wrong, inconsistent or unsafe.",
  },
  {
    q: "How does an engagement work?",
    a: "Every engagement is defined in writing before work begins: what we verify, what you receive, the standard the results must meet, and the price. Findings are traceable to their source, and results are judged only against the agreed standard.",
  },
  {
    q: "What happens to our data?",
    a: "Client data is used only for your engagement, never to train AI models or for other clients, and is deleted within 30 days of acceptance.",
  },
];

export default function App() {
  /* Restore homepage meta when navigating back from other routes. */
  useEffect(() => {
    setMeta({
      title: "Documore Integrity – Data, Decision & Agentic Integrity",
      description:
        "Documore verifies that your data, documents and AI systems agree before you act: lead list and CRM integrity, document reconciliation, and AI chatbot and agent validation.",
      url: "https://documore.com/",
    });
    return setJsonLd("faq", faqJsonLd(homeFaqs));
  }, []);

  /* The three pillars of Documore Integrity. Offer links come from offers.js. */
  const usecases = [
    {
      key: "data",
      tag: "Data Integrity",
      title: "Lead, CRM & Master Data Verification",
      promise: "Your records agree with each other — and with reality.",
      desc: "Verify lead lists, CRM records and master data across sources before your team acts on them.",
      items: ["Multi-source email verification", "Duplicate and stale record detection", "Employment checks before outreach", "Suppression against existing customers"],
      image: null,
      reverse: false,
    },
    {
      key: "decision",
      tag: "Decision Integrity",
      title: "Document & Invoice Reconciliation",
      promise: "Check the inputs before you quote, pay, or underwrite.",
      desc: "Turn invoices, BOMs, drawings and submissions into verified data, and detect mismatches before execution.",
      items: ["Invoice vs contract comparison", "BOM stock, price and lifecycle checks", "Document-to-data extraction with exceptions flagged", "Totals reconciled to source"],
      image: auditPreview,
      alt: "Document Validation",
      reverse: true,
    },
    {
      key: "agentic",
      tag: "Agentic Integrity",
      title: "AI Response & Agent Validation",
      promise: "Your AI says and does what it should.",
      desc: "Test chatbots, voice agents and RAG systems against your own policies and sources, and detect conflicting answers before they reach customers.",
      items: ["Multi-source validation", "Policy and conflict detection", "Failure patterns ranked into fixes", "Retrieval quality measured on your data"],
      image: aiPreview,
      alt: "AI Validation Dashboard",
      reverse: false,
    },
  ];

  const features = [
    { num: "01", title: "Cross-Source Comparison", text: "Compare multiple versions of the same data across systems, documents, or feeds." },
    { num: "02", title: "Mismatch Detection", text: "Identify inconsistencies before they lead to incorrect actions or decisions." },
    { num: "03", title: "Decision Safeguards", text: "Block, adjust, or flag decisions based on data alignment." },
  ];

  return (
    <>
      <style>{styles}</style>
      <div className="hp-root">

        

        {/* ── Hero ── */}
        <div className="hp-hero">
          <div>
            <div className="hp-hero-kicker">
              <span className="hp-hero-kicker-line" />
              <span className="hp-hero-kicker-text">Documore Integrity — Data · Decisions · Agents</span>
            </div>
            <h1 className="hp-hero-title">
              When Your Data<br />
              Isn't Aligned,<br />
              Decisions Can Be <em>Expensive</em>
            </h1>
          </div>
          <div className="hp-hero-right">
            <p className="hp-hero-deck">
              When your data comes from multiple sources, decisions are only as reliable as the agreement between them. Documore verifies that agreement — across every document, system, and feed — in real time, before you act. 
            </p>
            <p className="hp-hero-sub">
              Prevent costly disputes, misleading signals, and unreliable outputs.
            </p>
            <div className="hp-hero-ctas">
              <Link to="/services" className="hp-hero-cta">Explore Capabilities</Link>
              <a href="#contact" className="hp-hero-cta-ghost">Discuss Your Use Case</a>
            </div>
          </div>
        </div>

        {/* ── Divider ── */}
        <div className="hp-divider">
          <hr /><span className="hp-divider-ornament">§</span><hr />
        </div>

        {/* ── Problem ── */}
        <div className="hp-problem">
          <div className="hp-problem-left">
            <h2>When Data Doesn't Line Up, It Becomes a <em>Business Problem</em></h2>
          </div>
          <div className="hp-problem-right">
            <p>
              Most workflows rely on multiple sources of data — documents, APIs, or real-time feeds — that don't always agree.
            </p>
            <p>
              It shows up as incorrect outputs: wrong invoices, misleading analytics, mismatched records, or unreliable signals.
            </p>
            <div className="hp-problem-impact">
              Revenue leakage, operational errors, customer disputes, and loss of trust. The impact is direct.
            </div>
          </div>
        </div>

        {/* ── Divider ── */}
        <div className="hp-divider" style={{ marginTop: "0" }}>
          <hr /><span className="hp-divider-ornament">§</span><hr />
        </div>

        {/* ── How It Works ── */}
        <div id="how-it-works" className="hp-section-header">
          <div className="hp-section-kicker">
            <span className="hp-section-kicker-line" />
           <span className="hp-hero-kicker-text">Whatever the decision, whatever the industry — Documore it first.</span>
          </div>
          <h2 className="hp-section-title">Three kinds of integrity</h2>
        </div>

        <div className="hp-usecases">
          {usecases.map((uc) => (
            <div key={uc.key} id={uc.key}
              className={`hp-usecase${uc.reverse ? " reverse" : ""}${uc.image ? "" : " no-image"}`}>
              <div className="hp-usecase-body">
                <div className="hp-usecase-tag">{uc.tag}</div>
                <h3 className="hp-usecase-title">{uc.title}</h3>
                <p className="hp-usecase-promise">{uc.promise}</p>
                <p className="hp-usecase-desc">{uc.desc}</p>
                <ul className="hp-usecase-list">
                  {uc.items.map(item => <li key={item}>{item}</li>)}
                </ul>
                <div className="hp-usecase-offers">
                  <span className="hp-usecase-offers-label">Capabilities</span>
                  {offersByPillar(uc.key).map(o => (
                    <Link key={o.slug} to={`/services/${o.slug}`}>{o.name}</Link>
                  ))}
                </div>
              </div>
              {uc.image && (
                <div className="hp-usecase-image">
                  <img src={uc.image} alt={uc.alt} />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* ── Feature Cards ── */}
        <div className="hp-features" style={{ marginLeft: "auto", marginRight: "auto", maxWidth: "1200px", marginTop: "40px" }}>
          {features.map(f => (
            <div key={f.num} className="hp-feature">
              <div className="hp-feature-num">{f.num}</div>
              <div className="hp-feature-title">{f.title}</div>
              <div className="hp-feature-text">{f.text}</div>
            </div>
          ))}
        </div>

        {/* ── About ── */}
        <div id="about" className="hp-about">
          <div className="hp-about-inner">
            <div>
              <div className="hp-about-label">About</div>
              <h2 className="hp-about-heading">
                Built for systems that can't afford to <em>act on bad data</em>
              </h2>
            </div>
            <div className="hp-about-right">
              <p>
                Most systems are designed to store and process data. Very few are designed to <strong>verify whether that data is consistent</strong> across multiple sources.
              </p>
              <p>
                In real-world workflows, invoices, contracts, claims, and system records often tell different versions of the same story.
              </p>
              <p>
                Documore ensures your data agrees before your systems act on it.
              </p>
              <p>
                Documore was founded by <strong>Shaleen Kacker</strong>, a software architect with <strong>20 years of building systems in banking, insurance and information services</strong> — industries where records that disagree turn into disputes, losses and audit findings. <Link to="/about" style={{ color: "#e87d72" }}>More about us →</Link>
              </p>
            </div>
          </div>
        </div>

        {/* ── FAQ ── */}
        <div className="hp-section-header">
          <div className="hp-section-kicker">
            <span className="hp-section-kicker-line" />
            <span className="hp-section-kicker-text">Questions</span>
          </div>
          <h2 className="hp-section-title">Frequently asked</h2>
        </div>
        <div className="hp-faq">
          {homeFaqs.map(f => (
            <div key={f.q} className="hp-faq-item">
              <h3 className="hp-faq-q">{f.q}</h3>
              <p className="hp-faq-a">{f.a}</p>
            </div>
          ))}
        </div>

        {/* ── Contact ── */}
        <div id="contact" className="hp-contact">
          <div className="hp-contact-left">
            <h2>Let's Discuss Your <em>Use Case</em></h2>
            <p>
              Tell us about your system or workflow. We'll show you how Documore applies to your context.
            </p>
            <p>
              Or email <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
            </p>
          </div>

          <ContactForm />
        </div>

        <SiteFooter />

      </div>
    </>
  );
}
